import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { MembershipType } from '@court-sync/shared';
import { membershipTypeDiscountUpdateSchema } from '@court-sync/shared';
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
} from '@/components/ui';
import {
  DashboardDataCard,
  DashboardFilterCard,
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import { clubMembersService } from '@/services/clubMembers';
import { IdCard } from 'lucide-react';

interface MembershipDiscountsSectionProps {
  clubId: string;
}

export function MembershipDiscountsSection({ clubId }: MembershipDiscountsSectionProps) {
  const [types, setTypes] = useState<MembershipType[]>([]);
  const [discountInputs, setDiscountInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingTypeId, setSavingTypeId] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clubMembersService.listMembershipTypes(clubId);
      const data = res.success && res.data ? res.data : [];
      setTypes(data);
      setDiscountInputs(
        Object.fromEntries(data.map((type) => [type.id, String(type.courtHireDiscountPercent)]))
      );
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  const handleSaveDiscount = async (type: MembershipType) => {
    const raw = discountInputs[type.id] ?? '';
    const parsed = Number.parseFloat(raw);
    const validation = membershipTypeDiscountUpdateSchema.safeParse({
      courtHireDiscountPercent: parsed,
    });

    if (!validation.success) {
      const message = validation.error.issues[0]?.message ?? 'Enter a valid discount';
      setFieldErrors((prev) => ({ ...prev, [type.id]: message }));
      return;
    }

    setFieldErrors((prev) => ({ ...prev, [type.id]: '' }));
    setSavingTypeId(type.id);
    try {
      const res = await clubMembersService.updateMembershipType(clubId, type.id, {
        courtHireDiscountPercent: validation.data.courtHireDiscountPercent,
      });
      if (res.success) {
        toast.success(`Discount updated for ${type.name}`);
        await loadTypes();
      } else {
        toast.error(res.error || 'Failed to update discount');
      }
    } finally {
      setSavingTypeId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (types.length === 0) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={IdCard} />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No membership plans</h3>
        <p className="mb-4 text-gray-500 dark:text-gray-400">
          Create membership plans first, then set court hire discounts here.
        </p>
        <Link to="/admin/members?tab=types">
          <Button>Manage membership plans</Button>
        </Link>
      </DashboardEmptyStateCard>
    );
  }

  return (
    <DashboardFilterCard
      title="Membership court hire discounts"
      description="Set a discount percentage per membership plan. Not yet applied to court hire pricing — stored for when member discounts launch."
    >
      <DashboardDataCard className="border-0 shadow-none">
        <Table>
          <TableHeader>
            <TableRow className={dashboardTableHeaderRowClass}>
              <TableHead className={dashboardTableHeadClass}>Plan</TableHead>
              <TableHead className={dashboardTableHeadClass}>Discount %</TableHead>
              <TableHead className={dashboardTableHeadClass}>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id} className={dashboardTableDataRowClass}>
                <TableCell className={dashboardTableCellClass}>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{type.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {(type.priceCents / 100).toFixed(2)} {type.currency} / {type.billingInterval}
                    </p>
                  </div>
                </TableCell>
                <TableCell className={dashboardTableCellClass}>
                  <Field data-invalid={!!fieldErrors[type.id]}>
                    <FieldLabel htmlFor={`membership-discount-${type.id}`} className="sr-only">
                      Discount for {type.name}
                    </FieldLabel>
                    <Input
                      id={`membership-discount-${type.id}`}
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={discountInputs[type.id] ?? ''}
                      onChange={(e) =>
                        setDiscountInputs((prev) => ({ ...prev, [type.id]: e.target.value }))
                      }
                      aria-invalid={!!fieldErrors[type.id]}
                    />
                    <FieldError>{fieldErrors[type.id]}</FieldError>
                  </Field>
                </TableCell>
                <TableCell className={dashboardTableCellClass}>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleSaveDiscount(type)}
                    isLoading={savingTypeId === type.id}
                  >
                    Save
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DashboardDataCard>
      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/admin/members?tab=types" className="text-violet-600 hover:underline dark:text-violet-400">
          Manage full plan details
        </Link>{' '}
        (price, billing interval, and activation) on the Members page.
      </p>
    </DashboardFilterCard>
  );
}
